import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";

const KEY = "jyvis_cookie_consent";

const readConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch (e) {
    return null;
  }
};

const applyConsent = (consent) => {
  const ph = window.posthog;
  if (!ph || typeof ph.opt_out_capturing !== "function") return;
  if (consent?.analytics) ph.opt_in_capturing();
  else ph.opt_out_capturing();
};

export const CookieBanner = () => {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState(readConsent);
  const [open, setOpen] = useState(!readConsent());
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);

  useEffect(() => {
    applyConsent(consent);
    const t = setTimeout(() => applyConsent(readConsent()), 3000);
    const reopen = () => { setOpen(true); setCustom(true); };
    window.addEventListener("open-cookie-settings", reopen);
    return () => { clearTimeout(t); window.removeEventListener("open-cookie-settings", reopen); };
  }, [consent]);

  const save = (value) => {
    const c = { necessary: true, analytics: value, date: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(c));
    setConsent(c);
    setAnalytics(value);
    setOpen(false);
    setCustom(false);
  };

  if (!open || pathname.startsWith("/admin")) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-4" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <Cookie className="w-6 h-6 text-[#F97316] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-semibold text-gray-900 mb-1">Nous respectons votre vie privée</p>
            <p>Nous utilisons des cookies nécessaires au fonctionnement du site et, avec votre accord, des cookies de mesure d'audience.
              {" "}<Link to="/politique-de-confidentialite" className="text-[#102692] underline">En savoir plus</Link></p>
          </div>
        </div>
        {custom && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4 text-sm" data-testid="cookie-custom-panel">
            <div className="flex items-center justify-between">
              <span>Cookies nécessaires (toujours actifs)</span>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between">
              <span>Mesure d'audience</span>
              <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-analytics-switch" />
            </div>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 mt-4">
          {custom ? (
            <Button onClick={() => save(analytics)} className="bg-[#102692]" data-testid="cookie-save-btn">Enregistrer mes choix</Button>
          ) : (
            <Button variant="ghost" onClick={() => setCustom(true)} data-testid="cookie-customize-btn">Personnaliser</Button>
          )}
          <Button variant="outline" onClick={() => save(false)} data-testid="cookie-refuse-btn">Refuser</Button>
          <Button onClick={() => save(true)} className="bg-[#F97316] hover:bg-[#ea580c] text-white" data-testid="cookie-accept-btn">Accepter</Button>
        </div>
      </div>
    </div>
  );
};
